import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import compact from 'lodash/compact'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import type { FieldType } from 'modules/reports/components/Editor/Field/types'
import FieldSelectorModal from 'modules/reports/components/Editor/FieldSelectorModal'
import type FieldModel from 'modules/reports/store/models/Field'
import type ReportField from 'modules/reports/store/models/Field'
import type { AbilityType } from 'modules/reports/store/models/types'

import FieldsList from './FieldsList'
import Label from './Label'

export interface LabelType {
  text: string
  tooltipText?: string | React.ReactNode
  optional?: boolean
}

interface Props {
  fieldTypes: FieldType[]
  fieldAbilityTypes?: AbilityType[]
  excludeIdentifiers?: boolean
  excludeTypes?: string[]
  excludePaths?: string[]
  chartType?: string
  onSelectFields: (fields: ReportField[]) => void
  onRemoveField: (fieldType: FieldType) => void
  onReorderFields?: (items: FieldType[]) => void
  label: LabelType
  modalDescription: string
  modalTitle: string
  buttonLabel: string
  disabled?: boolean
  variables: unknown[]
  selectMultiple?: boolean
  isFilter?: boolean
  onClickItem?: (fields: ReportField[]) => void
  onClickButton?: () => void
  onCloseModal?: () => void
}

const EMPTY_ARRAY: string[] = []

const FieldSelector = ({
  fieldTypes,
  onSelectFields,
  label,
  modalDescription,
  modalTitle,
  excludeTypes,
  excludeIdentifiers = false,
  excludePaths,
  chartType,
  buttonLabel,
  onRemoveField,
  onReorderFields,
  fieldAbilityTypes,
  variables,
  isFilter = false,
  selectMultiple = false,
  disabled = false,
  onClickItem,
  onClickButton,
  onCloseModal: onClose,
}: Props) => {
  const [showModal, setShowModal] = React.useState(false)
  const compactedFieldTypes = useUnsafeMemo(() => compact(fieldTypes), [fieldTypes])
  const selectedFields: FieldModel[] = useUnsafeMemo(
    () => compact(compactedFieldTypes.map((fieldType) => fieldType.field)),
    [compactedFieldTypes]
  )
  const showButton = !compactedFieldTypes.length || selectMultiple
  const showFieldItems = !!compactedFieldTypes.length
  const onCloseModal = useUnsafeCallback(() => {
    setShowModal(false)
    onClose && onClose()
  }, [onClose])
  const onClickAddButton = useUnsafeCallback(() => {
    onClickButton && onClickButton()
    setShowModal(true)
  }, [onClickButton])

  const borderColor = disabled ? 'grey400' : 'radicalRed200'

  return (
    <FormRow>
      <Label optional={label.optional} tooltipText={label.tooltipText} text={label.text} />
      <Box
        borderRadius={{
          all: 'abs004',
        }}
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: borderColor,
          },
        }}
        padding="s12"
      >
        {showFieldItems && (
          <Box paddingBottom="s24" gap="s4">
            <FieldsList
              chartType={chartType}
              compactedFieldTypes={compactedFieldTypes}
              fieldAbilityTypes={fieldAbilityTypes}
              variables={variables}
              disabled={disabled}
              onRemoveField={onRemoveField}
              onReorderFields={onReorderFields}
            />
          </Box>
        )}
        {showButton && (
          <Box flexDirection="rowReverse">
            <Button onClick={onClickAddButton} disabled={disabled} type="secondary" iconName="add">
              {buttonLabel}
            </Button>
          </Box>
        )}
      </Box>
      {showModal && (
        <FieldSelectorModal
          onClickItem={onClickItem}
          title={modalTitle}
          description={modalDescription}
          onClose={onCloseModal}
          onSubmit={onSelectFields}
          excludeIdentifiers={excludeIdentifiers}
          excludeTypes={excludeTypes || EMPTY_ARRAY}
          excludePaths={excludePaths || EMPTY_ARRAY}
          chartType={chartType}
          selectMultiple={selectMultiple}
          selected={selectedFields}
          isFilter={isFilter}
        />
      )}
    </FormRow>
  )
}

export default observer(FieldSelector)
